// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React from 'react';

import FORM_KEYS from 'apps/web/constants/forms/lesson-plan/formKeys';

import { DLLFormat, UnitPlanPromptNames } from '@lessonPlanner/constants';

import {
  FRAMEWORK_OPTIONS,
  STATE_OPTIONS,
} from '../constants/forms/lesson-plan/selectFieldOptions';
import { OptionType, createOption, createOptions } from '../types/option';

import { SelectField } from './form-fields';
//TODO: Remove onblur and onchange props from these custom select fields once done testing
interface SelectProps {
  disabled?: boolean;
  value?: string;
  onChange?: (value: string) => void;
  error?: boolean;
  errorMessage?: string;
  onBlur?: () => void;
  hasCollege?: boolean;
  label?: string;
  isMultigrade?: boolean;
  isCurriculumBased?: boolean;
  curriculum?: UnitPlanPromptNames;
  updateFn?: (fieldName: string, value: unknown) => void;
  validateFn?: (fieldName: string) => void;
}

const GRADE_OPTIONS: OptionType[] = Array.from({ length: 12 }, (unused, i) =>
  createOption(`Grade ${i + 1}`),
);

const MULTIGRADE_OPTIONS: OptionType[] = Array.from(
  { length: Math.ceil(12 / 2) },
  (unused, i) => {
    const firstGrade = i * 2 + 1;
    const secondGrade = firstGrade + 1;
    return createOption(
      secondGrade <= 12
        ? `Grades ${firstGrade}-${secondGrade}`
        : `Grade ${firstGrade}`,
    );
  },
);

const DAY_OPTIONS: OptionType[] = createOptions([
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
]);

const renderOptions = (options: OptionType[]) =>
  options.map((option) => (
    <option key={option.value} value={option.value}>
      {option.label}
    </option>
  ));

const getCurriculumBasedGradeOptions = (curriculum: UnitPlanPromptNames) => {
  switch (curriculum) {
    case UnitPlanPromptNames.PYP:
      return Array.from({ length: 5 }, (unused, i) =>
        createOption(`Grade ${i + 1}`),
      );
    case UnitPlanPromptNames.MYP:
      return Array.from({ length: 5 }, (unused, i) =>
        createOption(`Grade ${i + 6}`),
      );
    case UnitPlanPromptNames.DP:
      return Array.from({ length: 2 }, (unused, i) =>
        createOption(`Grade ${i + 11}`),
      );
    default:
      return GRADE_OPTIONS;
  }
};

export function GradeLevelDropdown({
  disabled,
  value,
  error,
  errorMessage = 'Please select a grade level',
  dllFormat,
  hasCollege = false,
  label = 'Grade Level',
  isMultigrade = false,
  isCurriculumBased = false,
  curriculum,
  updateFn,
  validateFn,
  onBlur,
  onChange,
}: SelectProps & { dllFormat?: DLLFormat }) {
  if (isCurriculumBased && curriculum) {
    const shouldShowKindergarten = curriculum === UnitPlanPromptNames.PYP;
    const shouldShowCollege = curriculum === UnitPlanPromptNames.DP;
    const gradeOptions = getCurriculumBasedGradeOptions(curriculum);

    return (
      <SelectField
        className="col-span-full"
        disabled={disabled}
        error={error}
        errorMessage={errorMessage}
        id="gradeLevel"
        label={label}
        name="gradeLevel"
        updateFn={updateFn}
        validateFn={validateFn}
        value={value}
        onBlur={onBlur}
        onChange={onChange}
      >
        {shouldShowKindergarten && (
          <option value={'Kindergarten'}>Kindergarten</option>
        )}
        {renderOptions(gradeOptions)}
        {shouldShowCollege && <option value={'College'}>College</option>}
      </SelectField>
    );
  }

  const shouldShowKindergarten =
    (!dllFormat || dllFormat === DLLFormat.KINDER) && !isMultigrade;
  const shouldShowGradeOptions =
    !dllFormat ||
    dllFormat === DLLFormat.GRADE ||
    dllFormat === DLLFormat.MATATAG ||
    dllFormat === DLLFormat.STANDARD;
  const shouldShowCollege = hasCollege && !isMultigrade;

  return (
    <SelectField
      className="col-span-full"
      disabled={disabled}
      error={error}
      errorMessage={errorMessage}
      id="gradeLevel"
      label={label}
      name="gradeLevel"
      updateFn={updateFn}
      validateFn={validateFn}
      value={value}
      onBlur={onBlur}
      onChange={onChange}
    >
      {shouldShowKindergarten && (
        <option value={'Kindergarten'}>Kindergarten</option>
      )}
      {shouldShowGradeOptions &&
        renderOptions(isMultigrade ? MULTIGRADE_OPTIONS : GRADE_OPTIONS)}
      {shouldShowCollege && <option value={'College'}>College</option>}
    </SelectField>
  );
}

export function DayDropdown({
  disabled,
  value,
  error,
  errorMessage = 'Please select the day',
  updateFn,
  validateFn,
  onBlur,
  onChange,
}: SelectProps) {
  return (
    <SelectField
      className="col-span-full"
      disabled={disabled}
      error={error}
      errorMessage={errorMessage}
      id="day"
      label="Day"
      name="day"
      updateFn={updateFn}
      validateFn={validateFn}
      value={value}
      onBlur={onBlur}
      onChange={onChange}
    >
      {renderOptions(DAY_OPTIONS)}
    </SelectField>
  );
}

export function FrameworkDropdown({
  disabled,
  value = FRAMEWORK_OPTIONS[0].value,
  error,
  errorMessage = 'Please select a Framework',
  updateFn,
  validateFn,
  onBlur,
  onChange,
}: SelectProps) {
  return (
    <SelectField
      className="col-span-full"
      disabled={disabled}
      error={error}
      errorMessage={errorMessage}
      id={FORM_KEYS.curriculumFramework}
      label="Curriculum Framework"
      name="curriculumFramework"
      updateFn={updateFn}
      validateFn={validateFn}
      value={value}
      onBlur={onBlur}
      onChange={onChange}
    >
      {renderOptions(FRAMEWORK_OPTIONS)}
    </SelectField>
  );
}

export function StateDropdown({
  disabled,
  value,
  error,
  errorMessage = 'Please select a State Standard',
  updateFn,
  validateFn,
  onBlur,
  onChange,
}: SelectProps) {
  return (
    <SelectField
      className="col-span-full"
      disabled={disabled}
      error={error}
      errorMessage={errorMessage}
      id={FORM_KEYS.specificStandards}
      label="State Standard"
      name="specificStandards"
      updateFn={updateFn}
      validateFn={validateFn}
      value={value}
      onBlur={onBlur}
      onChange={onChange}
    >
      {renderOptions(STATE_OPTIONS)}
    </SelectField>
  );
}
